import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, formValueSelector, FormSection } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import messages from '../../../../../../text/messages';
import { birthdayToDateString, maskSSN } from './../../../../../../utilities/string';
import { formSettings } from './../settings';
import { TextField, BirthdayField, PhoneField, SelectField } from './../../../../../../components/fields';
import { required, birthdayRequired, email } from './../../../../../../utilities/validation';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import { setProgressionProperties } from '../../../../../../reducers/onboarding';
import { getAddresses, saveAddress } from '../../../../../../reducers/addresses';
import { updateUserDetails, updateUserProfile } from '../../../../../../reducers/identity';
import User from './../../../../../../models/user';
import Contractor from './../../../../../../models/contractor';
import Address from './../../../../../../models/address';
import mixpanel from 'mixpanel-browser';
import AddressComponent from '../../../../../../components/address';
import { mailingAddressFieldLabel, ssnFieldLabel, ssnFieldNormalize } from '../../../../../../utilities/regime';
import selectOptions from '../../../../../../text/selectOptions';

class PersonalInformationStep extends React.Component {
    constructor(props) {
        super(props);

        this.submit = this.submit.bind(this);
    }

    submit(values, onComplete) {
        const { role, profile, userDetails, updateUserDetails, updateUserProfile, saveAddress, setProgressionProperties, addresses } = this.props;
        const {
            firstName,
            lastName,
            middleName,
            noMiddleName,
            email,
            phoneNumber,
            dateOfBirth,
            socialSecurityNumber,
            mailingAddress,
            emergency_name,
            emergency_email,
            emergency_phone,
            emergency_relationship,
            gender
        } = values;

        let contractor = new Contractor(userDetails);
        contractor.phone = phoneNumber;
        contractor.tax_id = socialSecurityNumber;

        let userProfile = new User(profile);
        userProfile.first_name = firstName;
        userProfile.no_middle_name = noMiddleName;
        userProfile.middle_name = middleName && middleName !== '' ? middleName : null;
        userProfile.last_name = lastName;
        userProfile.email = email;
        userProfile.phone = phoneNumber;
        userProfile.birthday_date = dateOfBirth;
        userProfile.ssn_bg_check = socialSecurityNumber;
        userProfile.emergency_name = emergency_name;
        userProfile.emergency_email = emergency_email;
        userProfile.emergency_phone = emergency_phone;
        userProfile.emergency_relationship = emergency_relationship;
        userProfile.zip = mailingAddress && mailingAddress.zip;
        userProfile.gender = gender;

        const oldAddress = addresses.length ? addresses[addresses.length - 1] : {};
        let address = new Address(mailingAddress);

        updateUserDetails(role, contractor, () => {
            updateUserProfile({ ...userProfile, birthday_date: birthdayToDateString(dateOfBirth) }, () => {
                saveAddress({ ...address, id: oldAddress.id, ref_type: role.type, ref_id: role.id }, () => {
                    setProgressionProperties({ personalInformation: values });
                    setProgressionProperties({ mailingAddress: address });
                    onComplete();
                });
            });
        });
    }

    componentDidMount() {
        mixpanel.track('WorkerOnboarding', { _data: { branch: 'employee', step: 'PersonalInformationStep' } });

        const { role, profile, getAddresses, setProgressionProperties } = this.props;
        getAddresses(role, addresses => {
            let personalInformation = {
                firstName: profile.first_name,
                middleName: profile.middle_name,
                lastName: profile.last_name,
                email: profile.email,
                phoneNumber: profile.phone,
                dateOfBirth: profile.birthday_date,
                socialSecurityNumber: maskSSN(profile.ssn_bg_check),
                emergency_name: profile.emergency_name,
                emergency_email: profile.emergency_email,
                emergency_phone: profile.emergency_phone,
                emergency_relationship: profile.emergency_relationship,
                w4_fields: profile.w4_fields,
                i9_fields: profile.i9_fields,
                gender: profile.gender
            };

            let dateOfBirth = { day: null, month: null, year: null };
            if (personalInformation.dateOfBirth) {
                let date = new Date(personalInformation.dateOfBirth + 'T00:00:00');
                dateOfBirth = {
                    date: personalInformation.dateOfBirth,
                    day: isNaN(date.getDate()) ? null : date.getDate(),
                    month: isNaN(date.getMonth()) ? null : date.getMonth() + 1,
                    year: isNaN(date.getFullYear()) ? null : date.getFullYear()
                };
            }

            setProgressionProperties({ personalInformation: { ...personalInformation, dateOfBirth } });

            if (addresses && addresses.length > 0) {
                setProgressionProperties({ mailingAddress: addresses[addresses.length - 1] });
            }
        });
    }

    render() {
        const { handleSubmit, onSubmit, back, submitting, dateOfBirth, client, country, job } = this.props;

        const colWidth = 'col-xs-12 col-lg-10 col-xl-9';

        return (
            <form onSubmit={handleSubmit(values => this.submit(values, onSubmit))}>
                <div className="row section">
                    <div className="col-xs-12">
                        <input type="hidden" value="something" />
                        <FormattedMessage
                            id={'employeePersonalInfo.introText1'}
                            defaultMessage={'You know all those forms you have to fill out to start a new job? '}
                        />
                        <FormattedMessage
                            id={'employeePersonalInfo.introText2'}
                            defaultMessage={
                                "Well, we're going to fill most of them out for you, using the information you provide below. You only have to enter it once, so please ensure that it's correct!"
                            }
                        />
                    </div>
                </div>

                <div className="row section">
                    <div className="row">
                        <div className={colWidth}>
                            <div className="col-xs-12 col-sm-4">
                                <Field name="firstName" validate={required} component={TextField} placeholder={messages.firstName} />
                            </div>
                            <div className="col-xs-12 col-sm-4">
                                <Field name="middleName" component={TextField} placeholder={messages.middleName} />
                            </div>

                            <div className="col-xs-12 col-sm-4">
                                <Field name="lastName" validate={required} component={TextField} placeholder={messages.lastName} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className={colWidth}>
                            <div className="col-xs-12 col-sm-8">
                                <Field name="email" validate={[required, email]} component={TextField} placeholder={messages.email} disabled />
                            </div>
                            <div className="col-xs-12 col-sm-4">
                                <Field name="phoneNumber" validate={required} component={PhoneField} placeholder={messages.phoneNumber} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className={colWidth}>
                            <div className="col-xs-12 col-md-5">
                                <Field
                                    name="dateOfBirth"
                                    validate={birthdayRequired}
                                    component={BirthdayField}
                                    selectedDate={dateOfBirth}
                                    placeholder={messages.dateOfBirth}
                                />
                            </div>
                            <div className="col-xs-12 col-md-3">
                                <Field
                                    name="gender"
                                    validate={required}
                                    component={SelectField}
                                    placeholder={messages.gender}
                                    options={selectOptions.genders}
                                />
                            </div>
                            <div className="col-xs-12 col-md-4">
                                <Field
                                    name="socialSecurityNumber"
                                    validate={required}
                                    component={TextField}
                                    placeholder={ssnFieldLabel(job.regime)}
                                    normalize={ssnFieldNormalize(job.regime)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row section">
                    <div className="col-xs-12">
                        <FormattedMessage {...mailingAddressFieldLabel(job.regime)} tagName={'h5'} />
                    </div>
                    <div className={colWidth}>
                        <FormSection name={'mailingAddress'}>
                            <div className="row">
                                <AddressComponent
                                    form={formSettings.form}
                                    section={'mailingAddress'}
                                    hideName={true}
                                    countries={client.countries}
                                    oneLineAddress={true}
                                />
                            </div>
                        </FormSection>
                    </div>
                    <div className="row">
                        <div className={colWidth}>
                            <div className="col-xs-12">
                                <FormattedMessage {...messages.emergencyContact} tagName={'h5'} />
                            </div>
                            <div className="col-xs-12 col-sm-6">
                                <Field
                                    name="emergency_name"
                                    validate={required}
                                    component={TextField}
                                    placeholder={messages.emergencyName}
                                    autoComplete="emergency_name"
                                />
                            </div>
                            <div className="col-xs-12 col-sm-6">
                                <Field
                                    name="emergency_phone"
                                    validate={required}
                                    component={PhoneField}
                                    placeholder={messages.phoneNumber}
                                    autoComplete="emergency_phone"
                                    country={country}
                                />
                            </div>
                            <div className="col-xs-12 col-sm-6">
                                <Field name="emergency_relationship" validate={required} component={TextField} placeholder={messages.relationship} />
                            </div>
                            <div className="col-xs-12 col-sm-6">
                                <Field
                                    name="emergency_email"
                                    validate={[email, required]}
                                    component={TextField}
                                    placeholder={messages.emergencyEmail}
                                    autoComplete="emergency_email"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <WizardButtons disabled={submitting} back={{ action: back }} />
            </form>
        );
    }
}

PersonalInformationStep = reduxForm(formSettings)(PersonalInformationStep);

const selector = formValueSelector('contractor-onboarding-wizard');
const mapStateToProps = state => {
    let progression = state.onboarding.progression;
    let initialValues =
        progression && progression.personalInformation ? { ...progression.personalInformation, mailingAddress: progression.mailingAddress } : undefined;

    return {
        role: state.identity.role,
        profile: state.identity.profile,
        userDetails: state.identity.details,
        dateOfBirth: selector(state, 'dateOfBirth') ? selector(state, 'dateOfBirth') : null,
        country: selector(state, 'mailingAddress.country'),
        initialValues: initialValues,
        client: state.onboarding.client,
        job: state.jobs.list[0]
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            updateUserDetails: updateUserDetails,
            updateUserProfile: updateUserProfile,
            saveAddress: saveAddress,
            getAddresses: getAddresses,
            setProgressionProperties: setProgressionProperties
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(PersonalInformationStep);



// WEBPACK FOOTER //
// ./src/pages/onboarding/tenants/contractor/steps/employee/personalInformation.jsx